import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  fetchMembers,
  inviteMember,
  removeMember,
  revokeInvite,
  type TeamInvite,
  type TeamMember,
  updateMemberRole,
} from '../api.js';
import { confirmDialog } from './dialogs.js';

export interface TeamPanelProps {
  tenantId: string;
}

type Msg = { kind: 'ok' | 'err'; text: string } | null;

/**
 * Team management for a tenant's OWNER — list members, invite by email + role,
 * change a member's role, remove a member, and revoke pending invites. Owner
 * gated server-side (manageMembers); the owner row is protected from
 * change/remove. Non-owners never see this panel (App only renders it for the
 * owner role).
 */
export function TeamPanel({ tenantId }: TeamPanelProps): JSX.Element {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<Msg>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Invite composer.
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'viewer'>('admin');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchMembers(tenantId);
    setLoading(false);
    if (res.kind === 'ok') {
      setMembers(res.members);
      setInvites(res.invites);
    } else if (res.kind === 'forbidden') {
      setMsg({ kind: 'err', text: 'Only the owner can manage the team.' });
    } else if (res.kind === 'network') {
      setMsg({ kind: 'err', text: `Network: ${res.message}` });
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function invite(): Promise<void> {
    const e = email.trim();
    if (e.length === 0) return;
    setBusy('invite');
    setMsg(null);
    const res = await inviteMember(tenantId, e, role);
    setBusy(null);
    if (res.kind === 'ok') {
      const sent = (res.body as { emailSent?: boolean }).emailSent === true;
      setMsg({ kind: 'ok', text: sent ? `Invite sent to ${e}.` : `Invite created for ${e} (email not configured — copy the link from the server).` });
      setEmail('');
      void load();
    } else if (res.kind === 'conflict') {
      setMsg({ kind: 'err', text: 'That email is already a member.' });
    } else if (res.kind === 'forbidden') {
      setMsg({ kind: 'err', text: 'Only the owner can invite.' });
    } else {
      setMsg({ kind: 'err', text: 'Could not send the invite.' });
    }
  }

  async function changeRole(m: TeamMember, next: 'admin' | 'viewer'): Promise<void> {
    setBusy(`role-${m.customerId}`);
    const res = await updateMemberRole(tenantId, m.customerId, next);
    setBusy(null);
    if (res.kind === 'ok') void load();
    else setMsg({ kind: 'err', text: 'Could not change the role.' });
  }

  async function remove(m: TeamMember): Promise<void> {
    const ok = await confirmDialog({
      title: 'Remove member',
      message: `Remove ${m.email ?? m.customerId} from this fleet? They lose all access.`,
      destructive: true,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    setBusy(`rm-${m.customerId}`);
    const res = await removeMember(tenantId, m.customerId);
    setBusy(null);
    if (res.kind === 'ok') void load();
    else setMsg({ kind: 'err', text: 'Could not remove the member.' });
  }

  async function revoke(inv: TeamInvite): Promise<void> {
    setBusy(`inv-${inv.token}`);
    const res = await revokeInvite(tenantId, inv.token);
    setBusy(null);
    if (res.kind === 'ok') void load();
    else setMsg({ kind: 'err', text: 'Could not revoke the invite.' });
  }

  return (
    <div className="team-panel" data-testid="team-panel">
      <header className="team-panel__head">
        <h3 className="team-panel__heading">Team</h3>
        <p className="team-panel__sub">
          Invite teammates and control what they can do. Owners manage billing and the team;
          admins edit the launcher, devices, and broadcasts; viewers have read-only access.
        </p>
      </header>

      {msg !== null && (
        <p className={`team-panel__msg team-panel__msg--${msg.kind}`} role="status">
          {msg.text}
        </p>
      )}

      <section className="team-panel__invite">
        <label className="lookup__field">
          <span className="lookup__label">Invite by email</span>
          <input
            className="lookup__input"
            type="email"
            placeholder="teammate@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="team-invite-email"
          />
        </label>
        <label className="lookup__field">
          <span className="lookup__label">Role</span>
          <select
            className="lookup__input"
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
            data-testid="team-invite-role"
          >
            <option value="admin">Admin — edit launcher, devices, broadcasts</option>
            <option value="viewer">Viewer — read-only</option>
          </select>
        </label>
        <button
          type="button"
          className="team-panel__btn team-panel__btn--primary"
          disabled={busy === 'invite' || email.trim().length === 0}
          onClick={() => void invite()}
          data-testid="team-invite-submit"
        >
          {busy === 'invite' ? 'Sending…' : 'Send invite'}
        </button>
      </section>

      {loading ? (
        <p className="team-panel__loading">Loading team…</p>
      ) : (
        <table className="team-panel__table" data-testid="team-members">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.customerId}>
                <td>{m.email ?? m.customerId}</td>
                <td>
                  {m.isBillingOwner ? (
                    <span className="team-panel__badge team-panel__badge--owner">owner</span>
                  ) : (
                    <select
                      className="team-panel__role-select"
                      value={m.role === 'viewer' ? 'viewer' : 'admin'}
                      disabled={busy === `role-${m.customerId}`}
                      onChange={(e) => void changeRole(m, e.target.value as 'admin' | 'viewer')}
                      data-testid={`team-role-${m.customerId}`}
                    >
                      <option value="admin">admin</option>
                      <option value="viewer">viewer</option>
                    </select>
                  )}
                </td>
                <td>
                  {m.isBillingOwner ? (
                    <span className="team-panel__muted">—</span>
                  ) : (
                    <button
                      type="button"
                      className="team-panel__btn team-panel__btn--danger"
                      disabled={busy === `rm-${m.customerId}`}
                      onClick={() => void remove(m)}
                      data-testid={`team-remove-${m.customerId}`}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {invites.length > 0 && (
        <section className="team-panel__invites" data-testid="team-invites">
          <h4 className="team-panel__subheading">Pending invites</h4>
          <ul className="team-panel__invite-list">
            {invites.map((inv) => (
              <li key={inv.token} className="team-panel__invite-row">
                <span>
                  {inv.email} · <em>{inv.role}</em>
                </span>
                <button
                  type="button"
                  className="team-panel__btn team-panel__btn--danger"
                  disabled={busy === `inv-${inv.token}`}
                  onClick={() => void revoke(inv)}
                  data-testid={`team-revoke-${inv.token}`}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
